import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { queryKeys } from './queryClient';
import { documentKeys } from './documentService';
import type {
  ApplyExtractionRequest,
  ApplyExtractionResult,
  ContractExtractions,
  ExtractionDetail,
  ExtractionSummary,
} from '../types';

/**
 * Query keys for AI document extractions. `byContract` nests under the contract detail key, so the
 * document upload and delete mutations (which invalidate every `['contracts']` query) refresh the
 * pending review count too.
 */
export const extractionKeys = {
  all: ['extractions'] as const,
  byContract: (contractId: string) => [...queryKeys.contracts.detail(contractId), 'extractions'] as const,
  byDocument: (documentId: string) => ['extractions', 'document', documentId] as const,
  detail: (id: string) => ['extractions', 'detail', id] as const,
};

const fetchContractExtractions = async (contractId: string): Promise<ContractExtractions> => {
  return api.get<ContractExtractions>(`/contracts/${contractId}/extractions`);
};

/**
 * Hook: a contract's extractions and how many wait for review (GET /contracts/{id}/extractions)
 */
export const useContractExtractions = (contractId: string | undefined) => {
  return useQuery({
    queryKey: extractionKeys.byContract(contractId!),
    queryFn: () => fetchContractExtractions(contractId!),
    enabled: !!contractId,
    staleTime: 1000 * 60,
  });
};

/**
 * Hook: the document's latest extraction, or null when it has none (GET /documents/{id}/extraction, 204)
 */
export const useDocumentExtraction = (documentId: string | undefined) => {
  return useQuery({
    queryKey: extractionKeys.byDocument(documentId!),
    queryFn: async () => (await api.get<ExtractionSummary | ''>(`/documents/${documentId}/extraction`)) || null,
    enabled: !!documentId,
  });
};

/**
 * Hook: the review payload (GET /extractions/{id}). It never refetches on its own (focus, reconnect):
 * the reviewer's selection is built from the payload on screen and sends its version tokens, so a
 * silent refresh must not swap the data under the selection (plan D13a). Reload is explicit.
 */
export const useExtraction = (id: string | undefined) => {
  return useQuery({
    queryKey: extractionKeys.detail(id!),
    queryFn: () => api.get<ExtractionDetail>(`/extractions/${id}`),
    enabled: !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Apply, reject and extract never retry: the global mutation default retries once, which would resend
 * a refused apply (hiding its 409 or 400 from the reviewer) or start a second paid extraction.
 */
const NO_RETRY = { retry: false } as const;

/** Everything an apply, reject or extract can change: the contract, its documents, licenses and costs, the dashboard */
const invalidateAfterReview = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
  queryClient.invalidateQueries({ queryKey: documentKeys.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.licenses.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.vendors.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.costRecords.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  queryClient.invalidateQueries({ queryKey: extractionKeys.all });
};

/**
 * Hook: apply the reviewer's decisions (POST /extractions/{id}/apply)
 */
export const useApplyExtraction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...NO_RETRY,
    mutationFn: ({ id, request }: { id: string; request: ApplyExtractionRequest }) =>
      api.post<ApplyExtractionResult>(`/extractions/${id}/apply`, request),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(extractionKeys.detail(id), result.extraction);
      invalidateAfterReview(queryClient);
    },
  });
};

/**
 * Hook: reject the whole extraction (POST /extractions/{id}/reject)
 */
export const useRejectExtraction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...NO_RETRY,
    mutationFn: (id: string) => api.post<ExtractionDetail>(`/extractions/${id}/reject`),
    onSuccess: (detail, id) => {
      queryClient.setQueryData(extractionKeys.detail(id), detail);
      invalidateAfterReview(queryClient);
    },
  });
};

/**
 * Hook: extract a document on demand (POST /documents/{id}/extract); `schema` re-reads a mislabelled
 * document as the other kind, which supersedes its review
 */
export const useExtractDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    ...NO_RETRY,
    mutationFn: ({ documentId, schema }: { documentId: string; schema?: 'contract' | 'invoice' }) =>
      api.post<ExtractionSummary>(`/documents/${documentId}/extract${schema ? `?schema=${schema}` : ''}`),
    onSuccess: () => invalidateAfterReview(queryClient),
  });
};
